import { ToolCapability } from "../../models/FormModels";
import { Controller, UseFormReturn } from "react-hook-form";
import HeiCheckbox from "../Form/HeiCheckbox/HeiCheckbox";
import HeiGroupButton from "../Form/HeiGroupButton/HeiGroupButton";
import ToolIcon from "../ToolIcon/ToolIcon";
import { SelectOption } from "../../models/form";
import { IconSize } from "../../models/iconSize";
import { CapabilityTool } from "../../models/tools";
import { ProjectFormType } from "../../hooks/useCreateProject";
import styles from "./ToolAccess.module.scss";
import { useMemo } from "react";
import { Roles } from "../../models/roles";
import { FormLabel, MenuItem, Select, Tooltip } from "@mui/material";
import { Warning } from "@mui/icons-material";

interface ToolAccessProps {
  toolIndex: number;
  userIndex: number;
  tool: ToolCapability;
  form: UseFormReturn<ProjectFormType>;
  userCanEdit: boolean;
  isCreateMode?: boolean;
}

const ToolAccess = ({
  tool,
  toolIndex,
  userIndex,
  form,
  userCanEdit,
  isCreateMode = false,
}: ToolAccessProps): JSX.Element => {
  const { control, watch, setValue } = form;

  const userRoles = watch(`users.${userIndex}.roles`);
  const toolhasAccess = watch(
    `users.${userIndex}.toolCaps.${toolIndex}.hasAccess`
  );
  const toolCustomRole = watch(
    `users.${userIndex}.toolCaps.${toolIndex}.toolCustomRole`
  );
  const displayToolRoles = useMemo(
    () =>
      !(
        tool.capabilityToolKey === "MONITORING:NEWRELIC" &&
        !userRoles.includes(Roles["Team Member"])
      ),

    [userRoles, tool]
  );

  const toolCheckboxDisabled = !userCanEdit || !tool.toolCreationCompleted;
  const toolCheckboxLabel = tool.toolDisplayName ?? tool.toolName;
  const rolesGreaterThanFour = tool.availableRoles?.length > 4;

  return (
    <div key={toolIndex}>
      <div className={styles.toolAccess}>
        {!isCreateMode && (
          <HeiCheckbox
            disabled={toolCheckboxDisabled}
            control={control}
            name={`users.${userIndex}.toolCaps.${toolIndex}.hasAccess`}
            label={""}
          />
        )}
        <div className={styles.toolAccess__toolIcon}>
          <ToolIcon
            tool={tool.capabilityToolKey as CapabilityTool}
            size={IconSize.Medium}
            tooltipTitle={tool.toolName}
          />
        </div>
        <FormLabel
          disabled={toolCheckboxDisabled}
          className={styles.toolAccess__toolName}
        >
          {toolCheckboxLabel}
          {!tool.toolCreationCompleted && (
            <Tooltip
              title={`${toolCheckboxLabel} is currently pending creation. Adding new members is restricted until the tool is fully provisioned. You will be able to add users once the provisioning process is complete.`}
              arrow
            >
              <Warning sx={{ ml: 1 }} fontSize="small" color="warning" />
            </Tooltip>
          )}
        </FormLabel>
      </div>

      {tool.availableRoles?.length > 0 && displayToolRoles && toolhasAccess && (
        <table className={styles.toolAccess__toolsTable}>
          <tr>
            <td>
              Invite user for {tool.toolName} as a:
              {rolesGreaterThanFour ? (
                <Controller
                  name={`users.${userIndex}.toolCaps.${toolIndex}.toolCustomRole`}
                  control={control}
                  render={({ field }) => {
                    return (
                      <Select
                        style={{
                          width: "300px",
                          marginLeft: "8px",
                        }}
                        disabled={!userCanEdit}
                        size="small"
                        value={toolCustomRole || ""}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          setValue(
                            `users.${userIndex}.toolCaps.${toolIndex}.toolCustomRole`,
                            e.target.value
                          );
                        }}
                      >
                        {tool.availableRoles?.map((o) => (
                          <MenuItem key={o.name} value={o.name}>
                            {o.name}
                          </MenuItem>
                        ))}
                      </Select>
                    );
                  }}
                />
              ) : (
                tool.availableRoles?.map((roles) => {
                  return (
                    <div>
                      {roles.name} ({roles.cost})
                    </div>
                  );
                })
              )}
            </td>
            <td className={styles.toolAccess__toolsTable___itemAlight}>
              {!rolesGreaterThanFour && (
                <HeiGroupButton
                  disabled={!userCanEdit}
                  control={form.control}
                  options={tool.availableRoles?.map(
                    (r) => ({ label: r.name, value: r.name } as SelectOption)
                  )}
                  name={`users.${userIndex}.toolCaps.${toolIndex}.toolCustomRole`}
                />
              )}
            </td>
          </tr>
        </table>
      )}
    </div>
  );
};

export default ToolAccess;
